import React, { useEffect, useState } from 'react';
import { Form } from 'react-bootstrap';
import { Loading } from '@tunz/ifs-react-lib';

import {
	getReferentialByType,
	ProductGroup,
	Referential,
	ReferentialType,
} from '../../Referentials';

export type ReferentialRendererProps = {
	items: Referential[];
	onChange: (value: number | string) => void;
	defaultValue?: string;
};

type Props = {
	type: ReferentialType;
	group?: ProductGroup;
	onSelect?: (id: number | string, item: Referential) => void;
	renderer?: React.FC<ReferentialRendererProps>;
	defaultValue?: string;
};

const ReferentialSelector: React.FC<Props> = ({
	type,
	group,
	onSelect,
	renderer,
	defaultValue,
}: Props) => {
	const [loading, setLoading] = useState(true);
	const [items, setItems] = useState<Referential[]>([]);

	useEffect(() => {
		getReferentialByType(type, group)
			.then((refs) => {
				setItems(refs);
			})
			.finally(() => {
				setLoading(false);
			});
	}, [group, type]);

	const onChange = (value: number | string): void => {
		const valueToNumber = Number(value);

		if (Number.isNaN(valueToNumber)) {
			// if value is a string
			const item = items.find((i: { name: string }) => i.name === value);
			onSelect?.(value, item as Referential);
		} else {
			// if value is a stringified number
			const item = items.find((i: { id: number }) => i.id === valueToNumber);
			onSelect?.(valueToNumber, item as Referential);
		}
	};

	const Renderer = renderer as React.FC<ReferentialRendererProps>;

	return loading ? (
		<Loading />
	) : (
		<div>
			{Renderer ? (
				<Renderer items={items} onChange={onChange} defaultValue={defaultValue} />
			) : (
				<Form.Control
					as="select"
					onChange={(e) => onChange(e.target.value)}
					data-testid="referentialSelector"
				>
					{items.map((i) => (
						<option key={i.id} value={i.id}>
							{`${i.id} - ${i.name}`}
						</option>
					))}
				</Form.Control>
			)}
		</div>
	);
};

export default ReferentialSelector;
